import { useEffect, useState, useCallback } from 'react';
import http from '../services/http';
import { Sheet } from '../types/sheet';
import { accountApiPath } from '@/config/deutscheBankDemo';

type UseSheetsResult = {
  sheets: Sheet[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

const sheetsCache = new Map<string, Sheet[]>();
const pendingSheetsRequests = new Map<string, Promise<Sheet[]>>();

export const useSheets = (accountId?: string | null): UseSheetsResult => {
  const [sheets, setSheets] = useState<Sheet[]>(() => {
    if (accountId && sheetsCache.has(accountId)) return sheetsCache.get(accountId)!;
    return [];
  });
  const [loading, setLoading] = useState<boolean>(() => !!accountId);
  const [error, setError] = useState<Error | null>(null);
  const load = useCallback(async () => {
    if (!accountId) {
      setSheets([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let promise = pendingSheetsRequests.get(accountId);

      if (!promise) {
        promise = (async () => {
          const res = await http.get(accountApiPath(accountId, '/sheets'));
          return res.data as Sheet[];
        })();
        pendingSheetsRequests.set(accountId, promise);
      }

      const data = await promise;
      sheetsCache.set(accountId, data);
      setSheets(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch sheets'));
      setSheets([]);
    } finally {
      pendingSheetsRequests.delete(accountId);
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    let mounted = true;
    if (!accountId) {
      if (mounted) {
        setSheets([]);
        setLoading(false);
        setError(null);
      }
      return;
    }

    // If we have cache, set it immediately then still trigger background refresh
    if (sheetsCache.has(accountId)) {
      setSheets(sheetsCache.get(accountId)!);
      setLoading(true);
      // proceed to load to refresh cache
      load();
      return;
    }

    // No cache: load and populate
    load();

    return () => {
      mounted = false;
    };
  }, [accountId, load]);

  const refetch = useCallback(async () => {
    // clear cache so load actually fetches
    if (accountId) sheetsCache.delete(accountId);
    await load();
  }, [accountId, load]);

  return { sheets, loading, error, refetch };
};

export default useSheets;
